import React, { useState, useEffect } from "react";
import { Layout, ConfigProvider, theme as antdTheme } from "antd";
import Sidebar from "./components/Sidebar";
import HeaderComponent from "./components/Header";
import Accueil from "./components/Accueil";
import CoursesPage from "./components/CoursesPage";
import CourseDetailPage from "./components/CourseDetailPage";
import CasesPage from "./components/CasesPage";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import CasePage from "./components/Case";
import Login from "./components/Login";
import RequireAuth from "./components/RequireAuth";
import ErrorBoundary from "./components/ErrorBoundary";
import { Result, Button } from "antd";
import { AppProvider, useAppContext } from "./context/AppContext";
import { listCourses } from "./api/courses";
import { listCases } from "./api/cases";
import { listFavorites } from "./api/favorites";

const { Content } = Layout;

const LegacyCoursesRedirect = ({ filter }) => {
  const { userRole } = useAppContext();
  const target =
    filter === "favoris"
      ? "/cours?filter=favoris"
      : userRole === "Professeur"
      ? "/cours?filter=mine"
      : "/cours";
  return <Navigate to={filter === "favoris" ? "/cours?filter=favoris" : target} replace />;
};

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { isDarkMode, professorName } = useAppContext();

  const location = useLocation();

  useEffect(() => {
    if (
      location.pathname === "/blank" ||
      location.pathname.startsWith("/cas/")
    ) {
      setCollapsed(true);
    }
  }, [location.pathname]);

  const headerHeight = 64;

  const [courseList, setCourseList] = useState([]);
  const [allCases, setAllCases] = useState([]);
  const [favoriteCourseIds, setFavoriteCourseIds] = useState([]);

  // Load all app data from the API once (MainLayout only mounts post-auth).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [courses, cases, favs] = await Promise.all([
          listCourses(),
          listCases(),
          listFavorites(),
        ]);
        if (!active) return;
        setCourseList(courses);
        setAllCases(cases);
        setFavoriteCourseIds(favs.course_ids || []);
      } catch (_) {
        /* 401 handled by the API client (redirects to /login) */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const sharedCoursesProps = {
    professorName,
    courseList,
    setCourseList,
    favoriteCourseIds,
    setFavoriteCourseIds,
  };

  return (
    <ConfigProvider
      theme={{
        algorithm: isDarkMode
          ? antdTheme.darkAlgorithm
          : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: "#2563EB",
          colorInfo: "#2563EB",
          colorSuccess: "#10B981",
          colorWarning: "#F59E0B",
          colorError: "#EF4444",

          colorBgBase: isDarkMode ? "#0A0A0A" : "#FFFFFF",
          colorTextBase: isDarkMode ? "#FAFAFA" : "#0A0A0A",
          colorBgContainer: isDarkMode ? "#141414" : "#FFFFFF",
          colorBgElevated: isDarkMode ? "#1F1F1F" : "#FFFFFF",
          colorBgLayout: isDarkMode ? "#0A0A0A" : "#FAFAFA",

          colorBorder: isDarkMode ? "#262626" : "#E5E7EB",
          colorBorderSecondary: isDarkMode ? "#1F1F1F" : "#F3F4F6",

          borderRadius: 8,
          borderRadiusLG: 12,
          borderRadiusSM: 6,
          borderRadiusXS: 4,

          fontFamily:
            "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          fontSize: 14,
          fontSizeSM: 13,
          fontSizeLG: 16,
          fontSizeHeading1: 30,
          fontSizeHeading2: 24,
          fontSizeHeading3: 20,
          fontSizeHeading4: 16,

          controlHeight: 36,
          controlHeightSM: 28,
          controlHeightLG: 44,

          boxShadow: isDarkMode
            ? "none"
            : "0 1px 2px rgb(0 0 0 / 0.04), 0 1px 3px rgb(0 0 0 / 0.06)",
          boxShadowSecondary: isDarkMode
            ? "none"
            : "0 1px 2px rgb(0 0 0 / 0.04)",
          boxShadowTertiary: isDarkMode
            ? "none"
            : "0 1px 2px rgb(0 0 0 / 0.03)",

          wireframe: false,
        },
        components: {
          Layout: {
            headerBg: isDarkMode ? "#0A0A0A" : "#FFFFFF",
            siderBg: isDarkMode ? "#0A0A0A" : "#FAFAFA",
            bodyBg: isDarkMode ? "#0A0A0A" : "#FFFFFF",
            headerHeight: 64,
            headerPadding: "0 16px",
          },
          Menu: {
            itemBg: "transparent",
            subMenuItemBg: "transparent",
            itemSelectedBg: isDarkMode
              ? "rgba(37, 99, 235, 0.15)"
              : "rgba(37, 99, 235, 0.08)",
            itemSelectedColor: "#2563EB",
            itemHoverBg: isDarkMode
              ? "rgba(255, 255, 255, 0.04)"
              : "rgba(0, 0, 0, 0.04)",
            itemBorderRadius: 6,
            itemMarginInline: 8,
            itemHeight: 36,
          },
          Button: {
            primaryShadow: "none",
            defaultShadow: "none",
            dangerShadow: "none",
            fontWeight: 500,
          },
          Card: {
            borderRadiusLG: 12,
          },
          Modal: {
            borderRadiusLG: 12,
          },
          Input: {
            activeBorderColor: "#2563EB",
            hoverBorderColor: "#2563EB",
            activeShadow: "0 0 0 3px rgba(37, 99, 235, 0.12)",
          },
          Select: {
            optionSelectedBg: isDarkMode
              ? "rgba(37, 99, 235, 0.15)"
              : "rgba(37, 99, 235, 0.08)",
          },
          Table: {
            headerBg: isDarkMode ? "#141414" : "#FAFAFA",
            rowHoverBg: isDarkMode ? "#1F1F1F" : "#F9FAFB",
            borderColor: isDarkMode ? "#262626" : "#E5E7EB",
          },
          Tabs: {
            inkBarColor: "#2563EB",
            itemSelectedColor: "#2563EB",
            itemHoverColor: "#2563EB",
          },
          Tag: {
            defaultBg: isDarkMode ? "#1F1F1F" : "#F3F4F6",
            defaultColor: isDarkMode ? "#FAFAFA" : "#374151",
          },
        },
      }}
    >
      <Layout style={{ height: "100vh", overflow: "hidden" }}>
        <Sidebar collapsed={collapsed} onCollapse={setCollapsed} />

        <Layout>
          <HeaderComponent
            onMenuClick={() => setCollapsed(!collapsed)}
            style={{
              position: "sticky",
              top: 0,
              width: "100%",
              zIndex: 10,
              height: `${headerHeight}px`,
            }}
          />

          <Content
            style={{
              height: `calc(100vh - ${headerHeight}px)`,
              padding: 12,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ flex: 1, overflow: "auto" }}>
              <Routes>
                <Route
                  path="/blank"
                  element={<CasePage collapsed={collapsed} allCases={allCases} />}
                />
                <Route
                  path="/cas/:caseId"
                  element={<CasePage collapsed={collapsed} allCases={allCases} />}
                />

                {/* Accueil — role-aware */}
                <Route
                  path="/"
                  element={
                    <Accueil
                      professorName={professorName}
                      courseList={courseList}
                      allCases={allCases}
                      setAllCases={setAllCases}
                      favoriteCourseIds={favoriteCourseIds}
                    />
                  }
                />

                {/* Cours unifié */}
                <Route path="/cours" element={<CoursesPage {...sharedCoursesProps} />} />
                <Route
                  path="/cours/:courseId"
                  element={
                    <CourseDetailPage
                      {...sharedCoursesProps}
                      allCases={allCases}
                      setAllCases={setAllCases}
                    />
                  }
                />

                {/* Cas (ex-Explorer) */}
                <Route
                  path="/cas"
                  element={
                    <CasesPage
                      professorName={professorName}
                      courseList={courseList}
                      allCases={allCases}
                      setAllCases={setAllCases}
                    />
                  }
                />

                {/* Redirections legacy */}
                <Route
                  path="/mes-cours"
                  element={<LegacyCoursesRedirect filter="favoris" />}
                />
                <Route
                  path="/catalogue-cours"
                  element={<Navigate to="/cours" replace />}
                />
                <Route path="/explorer" element={<Navigate to="/cas" replace />} />
              </Routes>
            </div>
          </Content>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
};

const appCrashFallback = (error) => (
  <Result
    status="error"
    title="Une erreur est survenue"
    subTitle={error?.message || String(error)}
    extra={
      <Button type="primary" onClick={() => window.location.assign("/")}>
        Retour à l'accueil
      </Button>
    }
  />
);

const App = () => (
  <Router>
    <AppProvider>
      <ErrorBoundary fallback={appCrashFallback}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/*"
            element={
              <RequireAuth>
                <MainLayout />
              </RequireAuth>
            }
          />
        </Routes>
      </ErrorBoundary>
    </AppProvider>
  </Router>
);

export default App;
